import React, { useEffect, useState } from 'react';
import { BellIcon, InfoIcon, Loader2Icon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthProvider';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
type NotificationType = {
  id: string;
  name: string;
  description: string;
  options: {
    email: boolean;
    sms: boolean;
    inApp: boolean;
    canDisable: boolean;
  };
};
export const NotificationPreferencesSection = () => {
  const {
    user
  } = useAuth();
  // State for notification preferences
  const [notificationTypes, setNotificationTypes] = useState<NotificationType[]>([{
    id: 'new-order',
    name: 'New Order Received',
    description: 'When someone purchases one of your products',
    options: {
      email: true,
      sms: true,
      inApp: true,
      canDisable: false
    }
  }, {
    id: 'customer-message',
    name: 'Customer Messages',
    description: 'When a buyer sends you a message or question',
    options: {
      email: true,
      sms: false,
      inApp: true,
      canDisable: false
    }
  }, {
    id: 'reviews-ratings',
    name: 'Reviews & Ratings',
    description: 'When someone leaves a review on your product',
    options: {
      email: true,
      sms: false,
      inApp: true,
      canDisable: true
    }
  }, {
    id: 'payout-processed',
    name: 'Payout Processed',
    description: 'When your earnings have been sent to your payment method',
    options: {
      email: true,
      sms: true,
      inApp: true,
      canDisable: false
    }
  }, {
    id: 'platform-updates',
    name: 'Platform Updates',
    description: 'Important announcements and new features',
    options: {
      email: true,
      sms: false,
      inApp: true,
      canDisable: true
    }
  }, {
    id: 'security-alerts',
    name: 'Security Alerts',
    description: 'Suspicious activity or security-related notifications',
    options: {
      email: true,
      sms: true,
      inApp: true,
      canDisable: false
    }
  }]);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<NotificationType[]>([...notificationTypes]);
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // We're assuming the phone is already verified as per requirements
  const [phoneVerified, setPhoneVerified] = useState(true);
  // Fetch notification preferences from Firestore
  useEffect(() => {
    const fetchNotificationPreferences = async () => {
      if (!user?.id) return;
      setIsLoading(true);
      setError(null);
      try {
        const preferencesDocRef = doc(db, 'userPreferences', user.id);
        const preferencesDoc = await getDoc(preferencesDocRef);
        if (preferencesDoc.exists()) {
          const data = preferencesDoc.data();
          // Initialize notification preferences from Firestore if they exist
          if (data.notificationPreferences) {
            setNotificationTypes(data.notificationPreferences);
            setFormData(data.notificationPreferences);
          }
        } else {
          // Try to create a new preferences document with defaults, but don't fail if it errors
          try {
            const defaultPreferences = {
              notificationPreferences: notificationTypes,
              createdAt: new Date(),
              userId: user.id
            };
            await setDoc(preferencesDocRef, defaultPreferences);
          } catch (writeErr) {
            console.warn('Could not create default preferences document:', writeErr);
          }
        }
      } catch (err) {
        // Silently fall back to default preferences if Firestore read fails
        console.warn('Could not load notification preferences, using defaults:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNotificationPreferences();
  }, [user]);
  const handleToggleChange = (notificationId: string, channel: 'email' | 'sms' | 'inApp', value: boolean) => {
    setFormData(prev => prev.map(notification => notification.id === notificationId ? {
      ...notification,
      options: {
        ...notification.options,
        [channel]: value
      }
    } : notification));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const preferencesDocRef = doc(db, 'userPreferences', user.id);
      // Get current data to update only notification preferences
      const preferencesDoc = await getDoc(preferencesDocRef);
      if (preferencesDoc.exists()) {
        // Update only the notificationPreferences field
        await updateDoc(preferencesDocRef, {
          notificationPreferences: formData,
          updatedAt: new Date()
        });
      } else {
        // Create a new document if it doesn't exist
        await setDoc(preferencesDocRef, {
          notificationPreferences: formData,
          createdAt: new Date(),
          userId: user.id
        });
      }
      // Update local state
      setNotificationTypes([...formData]);
      setSuccessMessage('Notification preferences updated successfully!');
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating notification preferences:', err);
      setError('Failed to update preferences. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  if (isLoading) {
    return <div className="flex justify-center items-center min-h-[300px]" data-id="element-3225">
        <Loader2Icon className="h-8 w-8 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-3226" />
        <span className="ml-2 text-gray-600 dark:text-[#B0C1BD]" data-id="element-3227">
          Loading preferences...
        </span>
      </div>;
  }
  return <div data-id="element-3228">
      <div className="flex justify-between items-center mb-6" data-id="element-3229">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-3230">
          Notification Preferences
        </h2>
        <button onClick={() => {
        setIsEditing(!isEditing);
        if (!isEditing) {
          // Reset form data when starting to edit
          setFormData([...notificationTypes]);
          setError(null);
          setSuccessMessage(null);
        }
      }} className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" disabled={isSaving} data-id="element-3231">
          {isEditing ? 'Cancel' : 'Edit Preferences'}
        </button>
      </div>

      {/* Success message */}
      {successMessage && <div className="mb-6 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-md text-green-800 dark:text-green-400 flex items-start" data-id="element-3232">
          <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-3233" />
          <span data-id="element-3234">{successMessage}</span>
        </div>}

      {/* Error message */}
      {error && <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-md text-red-800 dark:text-red-400 flex items-start" data-id="element-3235">
          <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-3236" />
          <span data-id="element-3237">{error}</span>
        </div>}

      <form onSubmit={handleSubmit} data-id="element-3238">
        <div className="space-y-6 mb-8" data-id="element-3239">
          <div className="bg-white dark:bg-[#162825] rounded-lg border border-gray-200 dark:border-[#1A3330] overflow-hidden" data-id="element-3240">
            <div className="divide-y divide-gray-200 dark:divide-[#1A3330]" data-id="element-3241">
              {formData.map(notification => <div key={notification.id} className="p-5" data-id="element-3242">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-3243">
                    <div data-id="element-3244">
                      <h3 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3245">
                        {notification.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3246">
                        {notification.description}
                      </p>
                    </div>
                    {!isEditing && <div className="flex items-center gap-3" data-id="element-3247">
                        {notification.options.email && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400" data-id="element-3248">
                            Email
                          </span>}
                        {notification.options.sms && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400" data-id="element-3249">
                            SMS
                          </span>}
                        {notification.options.inApp && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400" data-id="element-3250">
                            In-app
                          </span>}
                        {!notification.options.email && !notification.options.sms && !notification.options.inApp && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400" data-id="element-3251">
                              Off
                            </span>}
                      </div>}
                  </div>
                  {isEditing && <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4" data-id="element-3252">
                      <div className="flex items-center" data-id="element-3253">
                        <input id={`${notification.id}-email`} name={`${notification.id}-email`} type="checkbox" checked={notification.options.email} onChange={e => handleToggleChange(notification.id, 'email', e.target.checked)} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] border-gray-300 dark:border-[#1A3330] rounded" data-id="element-3254" />
                        <label htmlFor={`${notification.id}-email`} className="ml-2 block text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-3255">
                          Email
                        </label>
                      </div>
                      <div className="flex items-center" data-id="element-3256">
                        <input id={`${notification.id}-sms`} name={`${notification.id}-sms`} type="checkbox" checked={notification.options.sms} onChange={e => handleToggleChange(notification.id, 'sms', e.target.checked)} disabled={!phoneVerified} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] border-gray-300 dark:border-[#1A3330] rounded disabled:opacity-50" data-id="element-3257" />
                        <label htmlFor={`${notification.id}-sms`} className="ml-2 block text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-3258">
                          SMS
                        </label>
                      </div>
                      <div className="flex items-center" data-id="element-3259">
                        <input id={`${notification.id}-inapp`} name={`${notification.id}-inapp`} type="checkbox" checked={notification.options.inApp} onChange={e => handleToggleChange(notification.id, 'inApp', e.target.checked)} disabled={!notification.options.canDisable} className="h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] border-gray-300 dark:border-[#1A3330] rounded disabled:opacity-50" data-id="element-3260" />
                        <label htmlFor={`${notification.id}-inapp`} className="ml-2 block text-sm text-gray-700 dark:text-[#B0C1BD]" data-id="element-3261">
                          In-app
                        </label>
                      </div>
                    </div>}
                </div>)}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-[#162825] rounded-lg p-4 border border-gray-200 dark:border-[#1A3330]" data-id="element-3262">
            <div className="flex items-start" data-id="element-3263">
              <BellIcon className="w-5 h-5 text-gray-400 dark:text-[#7A8F8A] mr-3 mt-0.5 flex-shrink-0" data-id="element-3264" />
              <div data-id="element-3265">
                <h4 className="text-sm font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-3266">
                  About Notifications
                </h4>
                <p className="text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-3267">
                  Security alerts cannot be disabled as they contain important
                  information about your account. Email notifications will be
                  sent to your account email address.
                </p>
              </div>
            </div>
          </div>
        </div>
        {isEditing && <div className="flex justify-end" data-id="element-3268">
            <button type="button" onClick={() => {
          setFormData([...notificationTypes]);
          setError(null);
          setSuccessMessage(null);
          setIsEditing(false);
        }} className="px-5 py-2.5 mr-3 border border-gray-300 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-all duration-200" disabled={isSaving} data-id="element-3269">
              Cancel
            </button>
            <button type="submit" className="px-5 py-2.5 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white font-medium rounded-lg hover:shadow-md transition-all duration-200 flex items-center" disabled={isSaving} data-id="element-3270">
              {isSaving ? <>
                  <Loader2Icon className="h-4 w-4 mr-2 animate-spin" data-id="element-3271" />
                  Saving...
                </> : 'Save Changes'}
            </button>
          </div>}
      </form>
    </div>;
};